import { useState, useEffect } from 'react';
import { ChevronLeftIcon } from '../components/Icons';
import { updateProfile } from '../db';

export default function EditProfilePage({ onNavigate, user, onUserUpdate }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from the logged-in user
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);
    try {
      const updatedUser = await updateProfile(form);
      // Update in localStorage and propagate to parent
      const merged = { ...user, ...updatedUser };
      localStorage.setItem('user', JSON.stringify(merged));
      if (onUserUpdate) onUserUpdate(merged);
      setSuccess(true);
    } catch (err) {
      setError('Could not update your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF3F0]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 lg:px-8 pt-5 pb-4 bg-[#FAF3F0]">
        <button onClick={() => onNavigate('account')} className="p-1.5 -ml-1.5 text-[#F47B20]">
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#1A1A2E]">Edit Profile</h1>
      </div>

      <div className="px-4 lg:px-8 pb-28 lg:pb-12 max-w-2xl lg:max-w-4xl">
        <h2 className="text-xl font-black text-[#1A1A2E] mb-1">Your Personal Details</h2>
        <p className="text-gray-400 text-sm mb-5">Keep your contact details up to date.</p>



        {/* Form */}
        <form onSubmit={handleSubmit}>
          <p className="text-sm font-semibold text-[#1A1A2E] mb-3">Personal Details</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div className="relative">
              <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Full Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div className="relative">
              <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Your email address</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                placeholder="Email address"
              />
            </div>

            {/* Phone */}
            <div className="relative">
              <label className="absolute left-4 top-2 text-xs text-gray-400 z-10">Phone Number</label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                onFocus={(e) => { if (!e.target.value) setForm(f => ({ ...f, phone: '+234 ' })); }}
                onBlur={(e) => { if (e.target.value.trim() === '+234') setForm(f => ({ ...f, phone: '' })); }}
                className="w-full px-4 pt-7 pb-3 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] outline-none bg-white focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                placeholder="+234 80..."
              />
            </div>
          </div>

          {error && <p className="mt-3 text-red-500 text-sm">{error}</p>}
          {success && <p className="mt-3 text-green-600 text-sm font-medium">✓ Profile updated successfully!</p>}

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={() => onNavigate('account')}
              className="flex-1 lg:flex-none lg:px-8 py-3.5 rounded-2xl border-2 border-gray-200 text-[#1A1A2E] text-sm font-semibold hover:border-gray-300 transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 lg:flex-none lg:px-10 py-3.5 rounded-2xl bg-[#F47B20] hover:bg-[#D96A10] text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Submit Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
